import { Button } from "@appretio/ui-kit";
import { KeyboardEvent, useMemo, useState } from "react";
import { FeedbackPayload } from "../../hooks/useFeedbackPayload";
import * as styles from "./FeedbackTransparencyPane.css";

/** Props for FeedbackTransparencyPane. */
export interface FeedbackTransparencyPaneProps {
  /**
   * The capture a Send right now would submit. Null while the modal is
   * closed; a raw unserializable value still renders (see safeStringify).
   */
  payload: FeedbackPayload | null;
  /** How many screenshots are attached — named in the collapsed summary. */
  screenshotCount: number;
  /** Whether the form is locked (a submit is in flight). */
  isDisabled: boolean;
  /**
   * Called when the disclosure expands, so the caller re-collects the
   * capture: what the pane discloses must match what Send would submit.
   */
  onExpand: () => void;
}

/**
 * The GDPR "What will be sent" disclosure: a collapsed one-line summary of
 * the captured keys, and — once expanded — the exact JSON plus a Copy button.
 *
 * Every piece of its state (expanded, Copied) is deliberately LOCAL. The
 * Dialog unmounts its content on close, so this component unmounts with it
 * and a reopened modal always starts collapsed and un-copied — transient
 * state cannot outlive the session it was derived from
 * (docs/code-guidelines/stale-state.md).
 */
export function FeedbackTransparencyPane({
  payload,
  screenshotCount,
  isDisabled,
  onExpand
}: FeedbackTransparencyPaneProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // Serializing a big capture is not free — memoized so keystrokes in the
  // modal's message field never re-stringify the preview.
  const payloadText = useMemo(() => safeStringify(payload), [payload]);

  function handleSelectAll(event: KeyboardEvent<HTMLPreElement>) {
    // The pane is a <pre>, not a form field, so the browser default would
    // select the whole page/modal. Scope it to exactly the pane's JSON.
    // Containment at the modal boundary (stopPropagation) is the modal's
    // job — this handler only replaces the default selection.
    if (!((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "a"))
      return;
    event.preventDefault();
    const selection = window.getSelection();
    if (!selection) return;
    const range = document.createRange();
    range.selectNodeContents(event.currentTarget);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  return (
    <details
      className={styles.transparency}
      onToggle={(event) => {
        // Refresh the capture when the pane opens (cheap: once per toggle,
        // never per keystroke) — page state may have moved since the modal
        // opened. A fresh capture is also a fresh, un-copied text — reset
        // the Copy confirmation. Keyed on the actual open-state TRANSITION:
        // some DOM implementations dispatch redundant toggle events, and a
        // late duplicate must not re-collect a pane the user just copied
        // from.
        const open = event.currentTarget.open;
        if (open === isExpanded) return;
        setIsExpanded(open);
        if (open) {
          onExpand();
          setIsCopied(false);
        }
      }}
    >
      <summary className={styles.transparencySummary}>
        What will be sent: {describeCapturedKeys(payload, screenshotCount)}
      </summary>
      {/* Pane header — NOT inside <summary>: a button there fights the
          disclosure's native toggle activation. Only rendered while
          expanded, so a collapsed pane offers nothing clickable. */}
      {isExpanded && (
        <div className={styles.transparencyActions}>
          <Button
            type="button"
            size="sm"
            variant="tertiary"
            disabled={isDisabled}
            onClick={() => {
              // Clipboard API is absent in insecure contexts — the pane text
              // stays selectable (Cmd/Ctrl+A) either way. "Copied" only once
              // the async write RESOLVED; a rejected write copied nothing,
              // so the button must stay "Copy".
              navigator.clipboard
                ?.writeText(payloadText)
                .then(() => setIsCopied(true))
                .catch(() => setIsCopied(false));
            }}
          >
            {isCopied ? "Copied" : "Copy"}
          </Button>
        </div>
      )}
      <pre
        tabIndex={0}
        data-testid="feedback-preview-payload"
        className={styles.transparencyPayload}
        onKeyDown={handleSelectAll}
      >
        {payloadText}
      </pre>
    </details>
  );
}

/**
 * JSON.stringify that can never crash the render: captured values are
 * sanitized at capture time, but the transparency pane must survive even a
 * value that slipped through (circular refs, BigInt from a valueGetter).
 */
function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return `<payload not serializable in preview: ${
      error instanceof Error ? error.message : String(error)
    }>`;
  }
}

/** One-line summary of the captured keys for the collapsed disclosure. */
function describeCapturedKeys(
  captured: FeedbackPayload | null,
  screenshotCount: number
): string {
  if (!captured) return "";
  const keys = [
    "page",
    ...Object.keys(captured.context).map((key) => `context.${key}`),
    ...Object.keys(captured.client).map((key) => `client.${key}`)
  ];
  if (screenshotCount > 0) {
    keys.push(`${screenshotCount} screenshot(s)`);
  }
  return keys.join(", ");
}
